/**
 * The customers' own words, on the picture beside the sign-up form.
 *
 * One review at a time, in a card, and the next one takes its place by
 * itself. That is the one place on this site where something advances
 * without being asked, and it was asked for by name (see DESIGN.md, the
 * No Page Motion Rule). It is held to the same terms as the showreel:
 *
 *  - it plays only while it is on screen and the tab is showing;
 *  - the first time a mouse rests on it, anything in it takes focus, or it
 *    is pressed, it is the reader's, and it never moves by itself again
 *    (the Attention Rule: it does not resume behind their back);
 *  - with reduced motion it does not advance at all, and a dot swaps the
 *    words without a transition.
 *
 * Each review stays for as long as it takes to read: a one-line review and
 * a five-line one do not get the same few seconds.
 *
 * The words are carried whole from the same file the home page reads. No
 * headline of ours, no face, no rating.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { usePrefersReducedMotion } from '@/design-system/media'
import type { Testimonial } from '@/content/home'
/* The classes this writes live here; the repo's reachability guard wants the
   file that writes a ds- class to be the file that loads its stylesheet. */
import '@/design-system/auth.css'

/** The shortest a review is held, and what each character of it adds. */
const HOLD_FLOOR_MS = 6000
const HOLD_BASE_MS = 2500
const HOLD_PER_CHARACTER_MS = 45

/** How long a review stays before the next one takes its place. */
export function holdFor(review: Testimonial): number {
  return Math.max(HOLD_FLOOR_MS, HOLD_BASE_MS + review.said.length * HOLD_PER_CHARACTER_MS)
}

export function AuthReviews({ source, reviews }: { source: string; reviews: Testimonial[] }) {
  const still = usePrefersReducedMotion()
  const [at, setAt] = useState(0)
  /* Somebody pointed at it, focused it or pressed it: it is theirs from here on. */
  const [handed, setHanded] = useState(false)
  const [onScreen, setOnScreen] = useState(true)
  const [tabShowing, setTabShowing] = useState(true)
  const [height, setHeight] = useState<number | null>(null)
  const card = useRef<HTMLElement>(null)
  const stack = useRef<HTMLDivElement>(null)
  const count = reviews.length

  useEffect(() => {
    const el = card.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), { threshold: 0.4 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const read = () => setTabShowing(document.visibilityState !== 'hidden')
    read()
    document.addEventListener('visibilitychange', read)
    return () => document.removeEventListener('visibilitychange', read)
  }, [])

  const playing = !still && !handed && onScreen && tabShowing && count > 1

  useEffect(() => {
    if (!playing) return
    const timer = window.setTimeout(() => setAt((now) => (now + 1) % count), holdFor(reviews[at]))
    return () => window.clearTimeout(timer)
  }, [playing, at, count, reviews])

  /*
    The card is as tall as the review it is showing, and moves to the next
    height as the words change. Every review sits in the same grid cell, so
    without this the card would stand at the height of the longest one and
    a one-line review would float in an empty box.

    Unmeasured (the first paint, or anywhere layout is not computed) it
    falls back to exactly that, which is correct and only less tidy.
  */
  const measure = useCallback(() => {
    const showing = stack.current?.children[at] as HTMLElement | undefined
    const tall = showing?.offsetHeight ?? 0
    if (tall > 0) setHeight(tall)
  }, [at])

  useLayoutEffect(measure, [measure])

  useEffect(() => {
    const el = stack.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    Array.from(el.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [measure, count])

  const show = (index: number) => {
    setHanded(true)
    setAt(index)
  }

  return (
    <section
      ref={card}
      className="ds-auth__reviews"
      data-testid="auth-reviews"
      data-playing={playing ? 'true' : undefined}
      role="group"
      aria-roledescription="carousel"
      aria-label={source}
      /* A mouse arriving counts and a finger arriving does not: a touch
         fires enter while somebody is only scrolling past. A press counts
         from either. */
      onPointerEnter={(event) => event.pointerType === 'mouse' && setHanded(true)}
      onPointerDown={() => setHanded(true)}
      onFocus={() => setHanded(true)}
    >
      {/* Announced only once a reader is driving. Read out on a timer it
          would talk over somebody filling in the form beside it. */}
      <div
        ref={stack}
        className="ds-auth__quotes"
        style={height === null ? undefined : { height }}
        aria-live={handed ? 'polite' : 'off'}
      >
        {reviews.map((review, index) => (
          <figure
            key={review.who}
            className="ds-auth__quote"
            data-on={index === at ? 'true' : undefined}
            aria-hidden={index === at ? undefined : true}
          >
            <blockquote className="ds-auth__said">&ldquo;{review.said}&rdquo;</blockquote>
            <figcaption className="ds-auth__who">{review.who}</figcaption>
          </figure>
        ))}
      </div>
      <div className="ds-auth__reviewsfoot">
        <p className="ds-auth__from">{source}</p>
        {count > 1 && (
          <div className="ds-auth__dots">
            {reviews.map((review, index) => (
              <button
                key={review.who}
                type="button"
                className="ds-auth__dot"
                aria-label={`Show review ${index + 1} of ${count}`}
                aria-current={index === at ? 'true' : undefined}
                onClick={() => show(index)}
              >
                <span aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
